import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { paymentAPI, eventBookingAPI } from '../services/api';
import Footer from '../components/Footer';

const EventBookingConfirmation = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { user, isAuthenticated } = useAuth();
  const [bookingId, setBookingId] = useState('');
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [isVerifyingPayment, setIsVerifyingPayment] = useState(false);
  const [verificationError, setVerificationError] = useState(null);
  const [fetchedBookingData, setFetchedBookingData] = useState(null);

  const bookingData = fetchedBookingData || location.state?.bookingData;

  const transformEventBookingApiToState = (detail) => {
    const b = detail.booking || detail;
    const event = b.event;
    const show = b.show;
    const lineItems = b.lineItems || [];
    const ticketNameById = {};
    (location.state?.bookingData?.selectedTickets || []).forEach((t) => {
      if (t.id) ticketNameById[t.id] = t.name;
    });
    return {
      bookingId: b.bookingId,
      eventDetails: {
        id: b.eventId,
        title: event?.title,
        category: event?.category,
        location: event?.city || event?.state,
        venue: show?.venue || event?.address,
        image: event?.images?.[0],
        latitude: event?.latitude ?? null,
        longitude: event?.longitude ?? null,
        address: event?.address,
        city: event?.city,
        state: event?.state
      },
      showDetails: {
        id: b.showId,
        date: show?.date,
        time: show?.time,
        duration: show?.duration
      },
      selectedTickets: lineItems.map((li) => ({
        id: li.ticketTypeId,
        name: ticketNameById[li.ticketTypeId] || li.ticketTypeId,
        quantity: li.quantity,
        price: li.unitPrice?.amount,
        taxes: li.unitPrice?.taxes
      })),
      pricing: {
        subTotal: b.pricingSummary?.subTotal,
        taxes: b.pricingSummary?.taxes,
        total: b.pricingSummary?.grandTotal
      },
      user: {
        name: b.guestInfo?.name,
        email: b.guestInfo?.email,
        mobile: b.guestInfo?.mobile
      }
    };
  };

  const verifyPaymentStatus = async (orderId) => {
    try {
      setIsVerifyingPayment(true);
      setVerificationError(null);

      const verificationResult = await paymentAPI.verifyPayment(orderId);

      if (verificationResult.paymentStatus === 'SUCCESS') {
        setPaymentStatus('SUCCESS');
        const confirmedId = verificationResult.bookingId;
        setBookingId(confirmedId || '');
        if (confirmedId) {
          try {
            const detail = await eventBookingAPI.getEventBookingById(confirmedId);
            if (detail.booking) {
              setFetchedBookingData(transformEventBookingApiToState(detail));
            } else if (location.state?.bookingData) {
              setFetchedBookingData(location.state.bookingData);
            }
          } catch (fetchError) {
            console.error('Failed to fetch event booking:', fetchError);
            if (location.state?.bookingData) {
              setFetchedBookingData(location.state.bookingData);
            }
          }
        }
      } else if (verificationResult.paymentStatus === 'PENDING') {
        setPaymentStatus('PENDING');
        setVerificationError(
          verificationResult.paymentMessage ||
            `Payment is still ${verificationResult.orderStatus || 'in progress'}. Please complete payment or check back later.`
        );
      } else {
        setPaymentStatus('FAILED');
        setVerificationError(
          verificationResult.paymentMessage || 'Payment verification failed'
        );
      }
    } catch (error) {
      console.error('Event payment verification error:', error);
      setPaymentStatus('FAILED');
      setVerificationError(error.message || 'Failed to verify payment');
    } finally {
      setIsVerifyingPayment(false);
    }
  };

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const hashParams = new URLSearchParams(window.location.hash.substring(1));
    const orderIdFromStorage = sessionStorage.getItem('cashfree_order_id');
    const orderIdFromUrl =
      urlParams.get('order_id') ||
      urlParams.get('orderId') ||
      hashParams.get('order_id') ||
      searchParams.get('order_id') ||
      searchParams.get('orderId') ||
      location.state?.orderId ||
      location.state?.bookingData?.orderId ||
      orderIdFromStorage;

    if (orderIdFromUrl && orderIdFromUrl === orderIdFromStorage) {
      sessionStorage.removeItem('cashfree_order_id');
    }

    if (orderIdFromUrl) {
      verifyPaymentStatus(orderIdFromUrl);
    } else if (location.state?.bookingData?.orderId) {
      verifyPaymentStatus(location.state.bookingData.orderId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, location.state]);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const hasOrder =
      urlParams.get('order_id') ||
      urlParams.get('orderId') ||
      searchParams.get('order_id') ||
      location.state?.orderId ||
      location.state?.bookingData?.orderId;
    if (!bookingData && !hasOrder && paymentStatus !== 'PENDING') {
      navigate('/');
    }
  }, [bookingData, navigate, searchParams, location.state, paymentStatus]);

  const generateBarcodePattern = (id) => {
    if (!id) return [];

    const pattern = [];
    for (let i = 0; i < 60; i++) {
      const charIndex = i % id.length;
      const char = id[charIndex];
      const charCode = char.charCodeAt(0);
      const shouldBeBlack = (charCode + i) % 3 === 0 || (charCode + i) % 5 === 0 || (charCode + i) % 7 === 0;
      pattern.push(shouldBeBlack);
    }
    return pattern;
  };

  const handleClose = () => {
    navigate('/');
  };

  const urlParamsForPending = new URLSearchParams(window.location.search);
  const orderIdForPending =
    urlParamsForPending.get('order_id') ||
    urlParamsForPending.get('orderId') ||
    searchParams.get('order_id') ||
    searchParams.get('orderId') ||
    location.state?.orderId ||
    location.state?.bookingData?.orderId ||
    sessionStorage.getItem('cashfree_order_id');

  if (isVerifyingPayment) {
    return (
      <div className="min-h-screen bg-[#FF5E4C] flex items-center justify-center">
        <div className="text-white text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto mb-4" />
          <p>Verifying payment...</p>
        </div>
      </div>
    );
  }

  if (paymentStatus === 'PENDING' && !isVerifyingPayment) {
    return (
      <div className="min-h-screen bg-[#FF5E4C] p-[20px]">
        <div className="py-[20px]">
          <div className="flex items-center gap-4">
            <svg xmlns="http://www.w3.org/2000/svg" width="59" height="60" viewBox="0 0 59 60" fill="none">
              <circle cx="29.5" cy="30.3701" r="29.5" fill="white" />
              <path d="M29.5 15.37V30.37M29.5 45.37V30.37" stroke="#FF5E4C" strokeWidth="4.31825" strokeLinecap="round" />
              <circle cx="29.5" cy="30.37" r="3" fill="#FF5E4C" />
            </svg>
            <div className="text-[#FCFCFC] leading-none">
              <h1 className="text-[22px] font-medium">Payment In Progress</h1>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-[32px] p-[16px] shadow-sm">
          <div className="text-center py-8">
            <div className="text-orange-500 text-5xl mb-4">⏳</div>
            <h2 className="text-xl font-bold text-gray-900 mb-2">Payment Not Completed</h2>
            <p className="text-gray-600 mb-4 px-4">
              Your event booking is still pending until payment succeeds. Tickets are issued only after payment is confirmed.
            </p>
            {orderIdForPending && (
              <p className="text-sm text-gray-500 mb-6 px-4">Order ID: {orderIdForPending}</p>
            )}
            <div className="space-y-3">
              <button
                type="button"
                onClick={() => orderIdForPending && verifyPaymentStatus(orderIdForPending)}
                className="w-full bg-[#FF5E4C] text-white py-4 rounded-[14px] h-[56px] font-bold text-lg hover:bg-[#FF4A3A] transition-colors"
              >
                Check Payment Status
              </button>
              <button
                type="button"
                onClick={() => navigate('/')}
                className="w-full bg-gray-200 text-gray-800 py-4 rounded-[14px] h-[56px] font-bold text-lg hover:bg-gray-300 transition-colors"
              >
                Go Home
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (paymentStatus === 'FAILED') {
    return (
      <div className="min-h-screen bg-[#FF5E4C] p-[20px]">
        <div className="py-[20px]">
          <div className="flex items-center gap-4">
            <svg xmlns="http://www.w3.org/2000/svg" width="59" height="60" viewBox="0 0 59 60" fill="none">
              <circle cx="29.5" cy="30.3701" r="29.5" fill="white" />
              <path d="M18.75 18.37L40.25 40.37M40.25 18.37L18.75 40.37" stroke="#FF5E4C" strokeWidth="4.31825" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            <div className="text-[#FCFCFC] leading-none">
              <h1 className="text-[22px] font-medium">Booking Not Successful</h1>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-[32px] p-[16px] shadow-sm">
          <div className="text-center py-8">
            <div className="text-red-500 text-5xl mb-4">✕</div>
            <h2 className="text-xl font-bold text-gray-900 mb-2">Payment Failed</h2>
            <p className="text-gray-600 mb-6 px-4">
              {verificationError || 'Your payment could not be processed. Please try again from the event page.'}
            </p>
            <button
              type="button"
              onClick={() => navigate('/')}
              className="w-full bg-[#131313] text-white py-4 rounded-[14px] h-[56px] font-bold text-lg hover:bg-[#131313] transition-colors"
            >
              Go Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (paymentStatus === 'SUCCESS' && !bookingData) {
    return (
      <div className="min-h-screen bg-[#FF5E4C] p-[20px]">
        <div className="bg-white rounded-[32px] p-[16px] shadow-sm">
          <div className="text-center py-8">
            <p className="text-gray-700 mb-4">Payment succeeded but details could not be loaded.</p>
            {bookingId && <p className="text-sm text-gray-500 mb-4">Booking ID: {bookingId}</p>}
            <button
              type="button"
              onClick={() => navigate('/')}
              className="w-full bg-[#131313] text-white py-4 rounded-[14px] h-[56px] font-bold text-lg"
            >
              Go Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  const urlParamsEmpty = new URLSearchParams(window.location.search);
  const hasOrderInUrl =
    urlParamsEmpty.get('order_id') ||
    urlParamsEmpty.get('orderId') ||
    searchParams.get('order_id') ||
    location.state?.orderId ||
    location.state?.bookingData?.orderId;

  if (!bookingData && !hasOrderInUrl && paymentStatus !== 'SUCCESS') {
    return (
      <div className="min-h-screen bg-[#FF5E4C] flex items-center justify-center">
        <div className="text-white text-center">
          <p>No booking data found</p>
          <button type="button" onClick={() => navigate('/')} className="mt-4 px-4 py-2 bg-white text-[#FF5E4C] rounded">
            Go Home
          </button>
        </div>
      </div>
    );
  }

  if (paymentStatus !== 'SUCCESS' || !bookingData) {
    return null;
  }

  const getTotalTickets = () => {
    return bookingData.selectedTickets?.reduce((total, ticket) => total + (ticket.quantity || 1), 0) || 1;
  };

  const getTotalAmount = () => {
    if (bookingData.selectedTickets && bookingData.selectedTickets.length > 0) {
      return bookingData.selectedTickets.reduce((total, ticket) => {
        const ticketTotal = (ticket.price || 0) * (ticket.quantity || 1);
        return total + ticketTotal;
      }, 0);
    }
    return bookingData.pricing?.total || 0;
  };

  const getEventImage = () => {
    return bookingData.eventDetails?.image || '/assets/event-placeholder.jpg';
  };

  const getCoordinates = () => {
    const lat = bookingData.eventDetails?.latitude;
    const lng = bookingData.eventDetails?.longitude;

    if (lat == null || lng == null || isNaN(lat) || isNaN(lng)) {
      return null;
    }

    return { latitude: parseFloat(lat), longitude: parseFloat(lng) };
  };

  const getGoogleMapsUrl = () => {
    const coords = getCoordinates();
    if (coords) {
      return `https://www.google.com/maps?q=${coords.latitude},${coords.longitude}`;
    }
    const loc =
      bookingData.eventDetails?.venue ||
      bookingData.eventDetails?.address ||
      bookingData.eventDetails?.location ||
      '';
    if (loc) {
      return `https://www.google.maps/search/?api=1&query=${encodeURIComponent(loc)}`;
    }
    return null;
  };

  const handleMapClick = () => {
    const mapsUrl = getGoogleMapsUrl();
    if (mapsUrl) {
      window.open(mapsUrl, '_blank', 'noopener,noreferrer');
    }
  };

  const getUserDisplayName = () => {
    const displayName = bookingData?.user?.name || user?.name || 'Guest';
    const nameParts = displayName.split(' ');
    return {
      firstName: nameParts[0] || 'Guest',
      lastName: nameParts.slice(1).join(' ') || ''
    };
  };

  const userDisplayName = getUserDisplayName();
  const displayBookingId = bookingId || bookingData.bookingId || '';

  return (
    <div>
    <div className="min-h-screen md:min-h-fit bg-[#FF5E4C] p-[20px] md:px-[120px]">
      {/* Header Section - Coral Red Background */}
      <div className="py-[20px]">
        <div className="flex items-center gap-4">
          <svg xmlns="http://www.w3.org/2000/svg" width="59" height="60" viewBox="0 0 59 60" fill="none">
            <circle cx="29.5" cy="30.3701" r="29.5" fill="white" />
            <path d="M18.75 30.37L25.9162 37.5362L40.2485 23.2039" stroke="#FF5E4C" stroke-width="4.31825" stroke-linecap="round" stroke-linejoin="round" />
          </svg>

          {/* Header Text */}
          <div className="text-[#FCFCFC] leading-none">
            <p className="text-[16px] font-medium mb-[2px]">
              Hi {userDisplayName.firstName} {userDisplayName.lastName},
            </p>
            <h1 className="text-[22px] font-medium">Congrats on your booking</h1>
          </div>
        </div>
      </div>

      {/* Main Content Card */}
      <div className="bg-white w-full rounded-[32px] p-[16px] md:p-[64px] shadow-sm overflow-hidden flex flex-col md:flex-row">
        <div className="w-full md:w-[40%] md:mr-[64px] ">
          {/* Map Section - Clickable to open Google Maps */}
          <div className="relative rounded-[14px] bg-gray-100 h-[125px] overflow-hidden">
            <button
              onClick={handleMapClick}
              className="w-full h-full cursor-pointer hover:opacity-90 transition-opacity duration-200 group relative"
              title={getCoordinates() ? 'Click to open in Google Maps' : 'Click to search location in Google Maps'}
              aria-label="Open location in Google Maps"
            >
              {/* Map Background - Static Map Image (optional, requires API key) or placeholder */}
              <div className="w-full h-full relative">
                {getCoordinates() ? (
                  // Map-like background when coordinates are available
                  // Note: To show actual Google Maps embed, add your Google Maps API key:
                  // <iframe src={`https://www.google.com/maps/embed/v1/place?key=YOUR_API_KEY&q=${lat},${lng}&zoom=15`} />
                  <div className="w-full h-full bg-gradient-to-br from-blue-50 via-blue-100 to-gray-100 relative">
                    {/* Grid pattern to simulate map */}
                    <div className="absolute inset-0 opacity-20" style={{
                      backgroundImage: `
                        linear-gradient(rgba(0,0,0,0.1) 1px, transparent 1px),
                        linear-gradient(90deg, rgba(0,0,0,0.1) 1px, transparent 1px)
                      `,
                      backgroundSize: '20px 20px'
                    }}></div>
                  </div>
                ) : (
                  // Placeholder background when no coordinates
                  <div className="w-full h-full bg-gradient-to-br from-gray-200 to-gray-300"></div>
                )}
                
                {/* Location Pin */}
                <div className="absolute bg-[#FFD2CD20] rounded-full p-[15px] top-[50%] left-[50%] translate-x-[-50%] translate-y-[-50%] pointer-events-none group-hover:bg-[#FFD2CD40] transition-all duration-200 z-20">
                  <div className='bg-[#ff5e4c] rounded-full p-[7px]'>
                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="19" viewBox="0 0 18 19" fill="none">
                      <path d="M15.4648 7.20762C14.6773 3.74262 11.6548 2.18262 8.99983 2.18262C8.99983 2.18262 8.99983 2.18262 8.99233 2.18262C6.34483 2.18262 3.31483 3.73512 2.52733 7.20012C1.64983 11.0701 4.01983 14.3476 6.16483 16.4101C6.95983 17.1751 7.97983 17.5576 8.99983 17.5576C10.0198 17.5576 11.0398 17.1751 11.8273 16.4101C13.9723 14.3476 16.3423 11.0776 15.4648 7.20762ZM8.99983 10.9651C7.69483 10.9651 6.63733 9.90762 6.63733 8.60262C6.63733 7.29762 7.69483 6.24012 8.99983 6.24012C10.3048 6.24012 11.3623 7.29762 11.3623 8.60262C11.3623 9.90762 10.3048 10.9651 8.99983 10.9651Z" fill="#FEFEFE"/>
                    </svg>
                  </div>
                </div>
                <img src="https://bookforpaws-assets.s3.ap-south-1.amazonaws.com/features/map.png" alt="Property" className="w-full h-full absolute z-10 top-0 left-0 object-cover" />

                {/* Overlay hint text on hover */}
                <div className="absolute bottom-2 right-2 bg-black bg-opacity-60 text-white text-[10px] px-2 py-1 rounded pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-200 z-10">
                  Tap to open in Google Maps
                </div>
              </div>
            </button>
            
            {/* Event Preview Image */}
            <div className="absolute bottom-4 left-4 z-20 pointer-events-none">
              <img
                src={getEventImage()}
                alt="Event Preview"
                className="w-[50px] h-[50px] rounded-[9px] object-cover shadow-md"
              />
            </div>
          </div>

          {/* Event Information */}
          <div className="pt-[20px]">
            <h2 className="text-[16px] font-medium text-black leading-none">
              {bookingData.eventDetails?.title}
            </h2>
            <p className="text-[14px] text-[#78736A] font-medium leading-tight mb-[8px]">
              {getTotalTickets()} x Ticket{getTotalTickets() > 1 ? 's' : ''}
            </p>
            
            {/* Location */}
            <div className="flex items-center gap-[4px] ">
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="15" viewBox="0 0 14 15" fill="none">
                <g clipPath="url(#clip0_2904_5643)">
                  <path d="M5.25 7.28687C5.25 7.75099 5.43437 8.19611 5.76256 8.5243C6.09075 8.85249 6.53587 9.03687 7 9.03687C7.46413 9.03687 7.90925 8.85249 8.23744 8.5243C8.56563 8.19611 8.75 7.75099 8.75 7.28687C8.75 6.82274 8.56563 6.37762 8.23744 6.04943C7.90925 5.72124 7.46413 5.53687 7 5.53687C6.53587 5.53687 6.09075 5.72124 5.76256 6.04943C5.43437 6.37762 5.25 6.82274 5.25 7.28687Z" stroke="black" strokeWidth="1.16667" strokeLinecap="round" strokeLinejoin="round" />
                  <path d="M10.3006 10.5868L7.82549 13.0619C7.60673 13.2804 7.31016 13.4032 7.00094 13.4032C6.69173 13.4032 6.39516 13.2804 6.1764 13.0619L3.70074 10.5868C3.04812 9.93412 2.60368 9.1026 2.42364 8.19736C2.24359 7.29212 2.33602 6.35382 2.68924 5.50112C3.04245 4.64841 3.64059 3.91959 4.40802 3.40682C5.17544 2.89405 6.07768 2.62036 7.00065 2.62036C7.92362 2.62036 8.82586 2.89405 9.59329 3.40682C10.3607 3.91959 10.9589 4.64841 11.3121 5.50112C11.6653 6.35382 11.7577 7.29212 11.5777 8.19736C11.3976 9.1026 10.9532 9.93412 10.3006 10.5868Z" stroke="black" strokeWidth="1.16667" strokeLinecap="round" strokeLinejoin="round" />
                </g>
                <defs>
                  <clipPath id="clip0_2904_5643">
                    <rect width="14" height="14" fill="white" transform="translate(0 0.870117)" />
                  </clipPath>
                </defs>
              </svg>
              <span className="text-[13px] text-[#78736A] font-normal leading-tight truncate">{bookingData.eventDetails?.venue || bookingData.eventDetails?.location || 'Location not available'}</span>
            </div>
          </div>
        </div>
        <div className="w-full md:w-[40%]">
          {/* Divider */}
          <div className='flex my-[20px] md:mt-0 items-center justify-center'>
            <svg width="100%" height="1px">
              <line
                x1="0" y1="1" x2="100%" y2="1"
                stroke="#f0f0f0"
                strokeWidth="2"
                strokeDasharray="10 4"
              />
            </svg>
          </div>

          {/* Event Date/Time Details */}
          <div className="flex items-center justify-between">
            {/* Event Date */}
            <div className="flex flex-col gap-[6px] items-start ">
              <div className="flex items-center space-x-[4px]">
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
                  <g clipPath="url(#clip0_2846_6297)">
                    <path d="M2.33203 4.08341C2.33203 3.774 2.45495 3.47725 2.67374 3.25846C2.89253 3.03966 3.18928 2.91675 3.4987 2.91675H10.4987C10.8081 2.91675 11.1049 3.03966 11.3237 3.25846C11.5424 3.47725 11.6654 3.774 11.6654 4.08341V11.0834C11.6654 11.3928 11.5424 11.6896 11.3237 11.9084C11.1049 12.1272 10.8081 12.2501 10.4987 12.2501H3.4987C3.18928 12.2501 2.89253 12.1272 2.67374 11.9084C2.45495 11.6896 2.33203 11.3928 2.33203 11.0834V4.08341Z" stroke="black" strokeWidth="0.583333" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M9.33203 1.75V4.08333" stroke="black" strokeWidth="0.583333" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M4.66797 1.75V4.08333" stroke="black" strokeWidth="0.583333" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M2.33203 6.41675H11.6654" stroke="black" strokeWidth="0.583333" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M4.66797 8.75H5.83464V9.91667H4.66797V8.75Z" stroke="black" strokeWidth="0.583333" strokeLinecap="round" strokeLinejoin="round" />
                  </g>
                  <defs>
                    <clipPath id="clip0_2846_6297">
                      <rect width="14" height="14" fill="white" />
                    </clipPath>
                  </defs>
                </svg>
                <span className="text-[12px] text-[#999999]">Event Date</span>
              </div>
              <div className="">
                <div className="font-normal text-[18px] leading-none mb-[6px]">{bookingData.showDetails?.date}</div>
                <div className="text-[14px] font-medium leading-none text-[#636363]">{bookingData.showDetails?.time}</div>
              </div>
            </div>

            {/* Duration */}
            <div className="text-center">
              <span className="text-[#FF5E4C] font-medium text-[12px] leading-none">{bookingData.showDetails?.duration || '2 hours'}</span>
            </div>

            {/* Show Time */}
            <div className="flex flex-col gap-[6px] items-end">
              <div className="flex items-center space-x-[4px]">
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
                  <g clipPath="url(#clip0_2846_6297)">
                    <path d="M2.33203 4.08341C2.33203 3.774 2.45495 3.47725 2.67374 3.25846C2.89253 3.03966 3.18928 2.91675 3.4987 2.91675H10.4987C10.8081 2.91675 11.1049 3.03966 11.3237 3.25846C11.5424 3.47725 11.6654 3.774 11.6654 4.08341V11.0834C11.6654 11.3928 11.5424 11.6896 11.3237 11.9084C11.1049 12.1272 10.8081 12.2501 10.4987 12.2501H3.4987C3.18928 12.2501 2.89253 12.1272 2.67374 11.9084C2.45495 11.6896 2.33203 11.3928 2.33203 11.0834V4.08341Z" stroke="black" strokeWidth="0.583333" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M9.33203 1.75V4.08333" stroke="black" strokeWidth="0.583333" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M4.66797 1.75V4.08333" stroke="black" strokeWidth="0.583333" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M2.33203 6.41675H11.6654" stroke="black" strokeWidth="0.583333" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M4.66797 8.75H5.83464V9.91667H4.66797V8.75Z" stroke="black" strokeWidth="0.583333" strokeLinecap="round" strokeLinejoin="round" />
                  </g>
                  <defs>
                    <clipPath id="clip0_2846_6297">
                      <rect width="14" height="14" fill="white" />
                    </clipPath>
                  </defs>
                </svg>
                <span className="text-[12px] text-[#999999]">Show Time</span>
              </div>
              <div className="text-end">
                <div className="font-normal text-[18px] leading-none mb-[6px]">{bookingData.showDetails?.time}</div>
                <div className="text-[14px] font-medium leading-none text-[#636363]">{bookingData.showDetails?.duration || '2 hours'}</div>
              </div>
            </div>
          </div>

          {/* Divider */}
          <div className='flex my-[20px] items-center justify-center'>
            <svg width="100%" height="1px">
              <line
                x1="0" y1="1" x2="100%" y2="1"
                stroke="#f0f0f0"
                strokeWidth="2"
                strokeDasharray="10 4"
              />
            </svg>
          </div>

          {/* Guest, Ticket Type, and Phone Details */}
          <div className="">
            {/* Guest */}
            <div className="flex items-center py-[8px] justify-between">
              <div className="flex items-center gap-2">
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="21" viewBox="0 0 20 21" fill="none">
                  <path d="M10.0007 10.8702C12.3018 10.8702 14.1673 9.00472 14.1673 6.70353C14.1673 4.40235 12.3018 2.53687 10.0007 2.53687C7.69946 2.53687 5.83398 4.40235 5.83398 6.70353C5.83398 9.00472 7.69946 10.8702 10.0007 10.8702Z" stroke="#78828A" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  <path d="M17.1585 19.2035C17.1585 15.9785 13.9501 13.3701 10.0001 13.3701C6.05013 13.3701 2.8418 15.9785 2.8418 19.2035" stroke="#78828A" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                <span className="text-[14px] font-medium text-black">Guest</span>
              </div>
              <span className="text-[14px] text-black font-medium">{user?.name || 'Guest'}</span>
            </div>

            {/* Ticket Type */}
            <div className="flex items-start py-[8px]  justify-between">
              <div className="flex items-center gap-2">
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="21" viewBox="0 0 20 21" fill="none">
                  <path d="M1.66602 19.2034H18.3327" stroke="#78828A" strokeWidth="1.5" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round" />
                  <path d="M14.1667 2.53687H5.83333C3.33333 2.53687 2.5 4.02853 2.5 5.8702V19.2035H17.5V5.8702C17.5 4.02853 16.6667 2.53687 14.1667 2.53687Z" stroke="#78828A" strokeWidth="1.5" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round" />
                  <path d="M5.83398 14.6201H8.33398" stroke="#78828A" strokeWidth="1.5" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round" />
                  <path d="M11.666 14.6201H14.166" stroke="#78828A" strokeWidth="1.5" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round" />
                  <path d="M5.83398 10.8701H8.33398" stroke="#78828A" strokeWidth="1.5" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round" />
                  <path d="M11.666 10.8701H14.166" stroke="#78828A" strokeWidth="1.5" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round" />
                  <path d="M5.83398 7.12012H8.33398" stroke="#78828A" strokeWidth="1.5" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round" />
                  <path d="M11.666 7.12012H14.166" stroke="#78828A" strokeWidth="1.5" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                <span className="text-[14px] text-nowrap leading-none font-medium text-black">Ticket type</span>
              </div>
              <span className="text-[14px] leading-none text-wrap text-right text-black font-medium">
                {bookingData.selectedTickets?.map(ticket => `${ticket.quantity}x ${ticket.name}`).join(', ') || 'General Admission'}
              </span>
            </div>

            {/* Phone */}
            <div className="flex items-center py-[8px] justify-between">
              <div className="flex items-center gap-2">
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="21" viewBox="0 0 20 21" fill="none">
                  <path d="M18.3077 16.1452C18.3077 16.4452 18.241 16.7535 18.0993 17.0535C17.9577 17.3535 17.7744 17.6369 17.5327 17.9035C17.1244 18.3535 16.6743 18.6785 16.166 18.8869C15.666 19.0952 15.1244 19.2035 14.541 19.2035C13.691 19.2035 12.7827 19.0035 11.8243 18.5952C10.866 18.1869 9.90768 17.6369 8.95768 16.9452C7.99935 16.2452 7.09102 15.4702 6.22435 14.6119C5.36602 13.7452 4.59102 12.8369 3.89935 11.8869C3.21602 10.9369 2.66602 9.98687 2.26602 9.0452C1.86602 8.0952 1.66602 7.18687 1.66602 6.3202C1.66602 5.75353 1.76602 5.21187 1.96602 4.71187C2.16602 4.20353 2.48268 3.73687 2.92435 3.3202C3.45768 2.7952 4.04102 2.53687 4.65768 2.53687C4.89102 2.53687 5.12435 2.58687 5.33268 2.68687C5.54935 2.78687 5.74102 2.93687 5.89102 3.15353L7.82435 5.87853C7.97435 6.08687 8.08268 6.27853 8.15768 6.46187C8.23268 6.63687 8.27435 6.81187 8.27435 6.9702C8.27435 7.1702 8.21602 7.3702 8.09935 7.56187C7.99102 7.75353 7.83268 7.95353 7.63268 8.15353L6.99935 8.81187C6.90768 8.90353 6.86602 9.01187 6.86602 9.1452C6.86602 9.21187 6.87435 9.2702 6.89102 9.33687C6.91602 9.40353 6.94102 9.45353 6.95768 9.50353C7.10768 9.77853 7.36602 10.1369 7.73268 10.5702C8.10768 11.0035 8.50768 11.4452 8.94102 11.8869C9.39102 12.3285 9.82435 12.7369 10.266 13.1119C10.6993 13.4785 11.0577 13.7285 11.341 13.8785C11.3827 13.8952 11.4327 13.9202 11.491 13.9452C11.5577 13.9702 11.6243 13.9785 11.6993 13.9785C11.841 13.9785 11.9493 13.9285 12.041 13.8369L12.6743 13.2119C12.8827 13.0035 13.0827 12.8452 13.2743 12.7452C13.466 12.6285 13.6577 12.5702 13.866 12.5702C14.0243 12.5702 14.191 12.6035 14.3743 12.6785C14.5577 12.7535 14.7494 12.8619 14.9577 13.0035L17.716 14.9619C17.9327 15.1119 18.0827 15.2869 18.1743 15.4952C18.2577 15.7035 18.3077 15.9119 18.3077 16.1452Z" stroke="#78828A" strokeWidth="1.5" strokeMiterlimit="10" />
                </svg>
                <span className="text-[14px] font-medium text-black">Phone</span>
              </div>
              <span className="text-[14px] text-black font-medium">
                {user?.mobile || 'Not provided'}
              </span>
            </div>
          </div>
        </div>
        {/* Divider */}
        <div className='flex my-[20px] items-center justify-center md:hidden'>
          <svg width="100%" height="1px">
            <line
              x1="0" y1="1" x2="100%" y2="1"
              stroke="#f0f0f0"
              strokeWidth="2"
              strokeDasharray="10 4"
            />
          </svg>
        </div>
        {/* Divider */}
        <div className='hidden md:flex items-center justify-center mx-[64px]'>
          <svg width="1px" height="100%">
            <line
              x1="1" y1="0" x2="1" y2="100%"
              stroke="#f0f0f0"
              strokeWidth="2"
              strokeDasharray="10 4"
            />
          </svg>
        </div>

        {/* Barcode Section */}
        <div className="text-center flex flex-col w-full md:w-[10%] items-center justify-center rotate-0 md:rotate-[-90deg] ">
          {/* Barcode */}
          <div className="flex w-full md:w-fit justify-center mb-[10px] md:mb-0">
            <div className="bg-white p-0 max-w-[80%] md:max-w-fit rounded-lg ">
              {displayBookingId ? (
                <div className="flex space-x-[2px]">
                  {generateBarcodePattern(displayBookingId).map((isBlack, i) => (
                    <div
                      key={i}
                      className={`w-[2px] h-16 ${isBlack ? 'bg-black' : 'bg-white'
                        }`}
                    />
                  ))}
                </div>
              ) : (
                <div className="text-[#78828A] text-[12px] font-medium">Loading barcode...</div>
              )}
            </div>
          </div>

          {/* Booking ID */}
          <p className="text-[12px] text-[#78828A] font-medium">{displayBookingId}</p>
        </div>
      </div>

      {/* Footer Section - Close Button */}
      <div className="pt-[6px] md:hidden">
        <button
          onClick={handleClose}
          className="w-full bg-[#131313] text-white py-4 rounded-[14px] h-[56px] font-bold text-lg hover:bg-[#131313] transition-colors"
        >
          Close
        </button>
      </div>
      </div>

      {/* Footer - Desktop Only */}
      <div className="hidden md:block">
        <Footer />
      </div>

      {/* Debug Section - Development Only */}
      {process.env.NODE_ENV === 'development' && (
        <div className="fixed top-4 left-4 bg-black text-white p-4 text-xs rounded max-w-md max-h-96 overflow-auto">
          <div className="font-bold mb-2">Debug Info:</div>
          <div className="space-y-1">
          <div>Booking ID: {displayBookingId}</div>
            <div>Event: {bookingData.eventDetails?.title || 'N/A'}</div>
            <div>Authenticated: {isAuthenticated ? 'Yes' : 'No'}</div>
            <div>Current User: {user?.name || 'Not logged in'}</div>
            <div>Total Tickets: {getTotalTickets()}</div>
            <div>Total Amount: ₹{getTotalAmount().toLocaleString()}</div>
            <div>Selected Tickets: {bookingData.selectedTickets?.length || 0}</div>
            <div>Event Details: {bookingData.eventDetails ? 'Available' : 'Not available'}</div>
            <div>Show Details: {bookingData.showDetails ? 'Available' : 'Not available'}</div>
            <div>User: {bookingData.user ? 'Available' : 'Not available'}</div>
          </div>
        </div>
      )}
    
    </div>
  );
};

export default EventBookingConfirmation;